import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { motion } from "framer-motion";
import { Quote, ArrowLeft, Link2, Plus, Trash2, Download, Wand2 } from "lucide-react";
import axios from "axios";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_TYPES = [
  { value: "auto", label: "Auto-detect" },
  { value: "website", label: "Website" },
  { value: "journal", label: "Journal article" },
  { value: "book", label: "Book" },
  { value: "news", label: "News article" },
  { value: "video", label: "Video" },
  { value: "report", label: "Report" },
];

const STYLE_KEYS = [
  { key: "apa", label: "APA 7th", intext: "apa_intext" },
  { key: "mla", label: "MLA 9th", intext: "mla_intext" },
  { key: "chicago", label: "Chicago", intext: "chicago_intext" },
  { key: "harvard", label: "Harvard", intext: "harvard_intext" },
  { key: "ieee", label: "IEEE", intext: "ieee_intext" },
  { key: "vancouver", label: "Vancouver", intext: null },
  { key: "acs", label: "ACS", intext: null },
];

const STORAGE_KEY = "buddy_bibliography";

export default function CitationGeneratorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("manual"); // "manual" | "url" | "paste"
  const [text, setText] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [fields, setFields] = useState({ title: "", author: "", year: "", url: "", publisher: "", journal: "", volume: "", issue: "", pages: "", doi: "" });
  const [sourceType, setSourceType] = useState("auto");
  const [showInText, setShowInText] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [copied, setCopied] = useState(null);
  const [bibliography, setBibliography] = useState([]);
  const [activeStyle, setActiveStyle] = useState("apa");

  useEffect(() => {
    try { setBibliography(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")); } catch {}
  }, []);

  const persistBib = (next) => {
    setBibliography(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  };

  const updateField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const handleFetchUrl = async () => {
    if (!urlInput.trim()) return;
    setFetching(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/tools/citation-from-url`, { url: urlInput.trim() });
      setFields({
        title: data.title || "",
        author: data.author || "",
        year: data.year || "",
        url: data.url || urlInput.trim(),
        publisher: data.publisher || "",
        journal: "",
        volume: "",
        issue: "",
        pages: "",
        doi: data.doi || "",
      });
      if (data.source_type) setSourceType(data.source_type);
      setMode("manual"); // jump back to fields so user can review/edit
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Could not fetch metadata. Fill the fields manually.");
    } finally { setFetching(false); }
  };

  const handleGenerate = async () => {
    setLoading(true); setResult(null);
    const body = mode === "paste"
      ? { text: text.trim(), source_type: sourceType }
      : { ...fields, source_type: sourceType };
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/tools/citation-generate`, body);
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Citation generation failed.");
      setResult({ error: true, message: msg });
    } finally { setLoading(false); }
  };

  const handleCopy = (txt, id) => {
    try { navigator.clipboard.writeText(txt); } catch {}
    setCopied(id); setTimeout(() => setCopied(null), 1800);
  };

  const handleSaveToBibliography = () => {
    if (!result?.apa) return;
    const entry = {
      id: Date.now(),
      title: fields.title || (result.apa || "").slice(0, 80),
      author: fields.author,
      year: fields.year,
      ...result,
      saved_at: new Date().toISOString(),
    };
    persistBib([entry, ...bibliography].slice(0, 50));
  };

  const removeFromBib = (id) => persistBib(bibliography.filter((b) => b.id !== id));

  const exportBibliography = () => {
    if (bibliography.length === 0) return;
    const lines = bibliography.map((b, i) => `${i + 1}. [${activeStyle.toUpperCase()}] ${b[activeStyle] || b.apa}`).join("\n\n");
    const blob = new Blob([`Buddy Bibliography (${activeStyle.toUpperCase()})\nGenerated: ${new Date().toLocaleString()}\n\n${lines}`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `bibliography-${activeStyle}-${Date.now()}.txt`; a.click();
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}>
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 pt-6 pb-16">
        <button onClick={() => navigate("/ai-tools")} className="flex items-center gap-1 text-sm mb-4 hover:underline" style={{ color: "var(--rolex-green)" }} data-testid="back-to-tools"><ArrowLeft size={14} /> AI Tools</button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--info-bg)" }}><Quote size={20} style={{ color: "var(--info)" }} /></div>
          <div>
            <h1 className="font-display text-2xl font-bold" style={{ color: "var(--text-primary)" }}>Citation Generator</h1>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>APA · MLA · Chicago · Harvard · IEEE · Vancouver · ACS · paste a URL/DOI to auto-fill</p>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          {/* INPUT */}
          <div className="rounded-2xl p-6 space-y-4" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
            <div className="flex gap-2">
              {[{ id: "manual", label: "Enter Details" }, { id: "url", label: "URL / DOI" }, { id: "paste", label: "Paste Text" }].map(m => (
                <button key={m.id} onClick={() => setMode(m.id)} className="platform-pill flex-1 text-center text-xs" style={mode === m.id ? { background: "var(--info)", color: "white", borderColor: "var(--info)" } : {}} data-testid={`cite-mode-${m.id}`}>{m.label}</button>
              ))}
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: "var(--text-muted)" }}>Source Type</label>
              <select className="buddy-input text-xs py-2" value={sourceType} onChange={(e) => setSourceType(e.target.value)} data-testid="cite-source-type">
                {SOURCE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            {mode === "url" ? (
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: "var(--text-muted)" }}>URL or DOI</label>
                <div className="flex gap-2">
                  <input className="buddy-input text-xs py-2 flex-1" placeholder="https://example.com/article OR 10.1038/nature12373" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} data-testid="cite-url-input" />
                  <button onClick={handleFetchUrl} disabled={!urlInput.trim() || fetching} className="btn-primary text-xs px-4 py-2 flex items-center gap-1.5 shrink-0" style={{ background: "var(--info)", opacity: !urlInput.trim() ? 0.5 : 1 }} data-testid="cite-fetch-btn">
                    {fetching ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Link2 size={12} /> Fetch</>}
                  </button>
                </div>
                <p className="text-[11px]" style={{ color: "var(--text-light)" }}>Paste any article URL or DOI. We'll auto-fill the fields below.</p>
              </div>
            ) : mode === "paste" ? (
              <textarea className="buddy-input resize-none" rows={6} placeholder="Paste text containing source information…" value={text} onChange={(e) => setText(e.target.value)} data-testid="citation-text-input" />
            ) : (
              <div className="space-y-2.5">
                <input className="buddy-input text-xs py-2" placeholder="Title *" value={fields.title} onChange={(e) => updateField("title", e.target.value)} data-testid="citation-title" />
                <input className="buddy-input text-xs py-2" placeholder="Author(s) — e.g. Smith, J. or Smith, J. & Lee, K." value={fields.author} onChange={(e) => updateField("author", e.target.value)} data-testid="citation-author" />
                <div className="grid grid-cols-2 gap-2">
                  <input className="buddy-input text-xs py-2" placeholder="Year" value={fields.year} onChange={(e) => updateField("year", e.target.value)} data-testid="citation-year" />
                  <input className="buddy-input text-xs py-2" placeholder="Publisher" value={fields.publisher} onChange={(e) => updateField("publisher", e.target.value)} />
                </div>
                {(sourceType === "journal" || sourceType === "auto") && (
                  <div className="grid grid-cols-2 gap-2">
                    <input className="buddy-input text-xs py-2" placeholder="Journal" value={fields.journal} onChange={(e) => updateField("journal", e.target.value)} />
                    <input className="buddy-input text-xs py-2" placeholder="Vol(Issue), Pages — 12(3), 1-15" value={`${fields.volume}${fields.issue ? `(${fields.issue})` : ""}${fields.pages ? `, ${fields.pages}` : ""}`} onChange={(e) => {
                      const v = e.target.value;
                      const m = v.match(/^(\d+)?(?:\((\d+)\))?(?:,\s*(.+))?$/);
                      if (m) setFields((f) => ({ ...f, volume: m[1] || "", issue: m[2] || "", pages: m[3] || "" }));
                    }} />
                  </div>
                )}
                <input className="buddy-input text-xs py-2" placeholder="DOI (e.g. 10.1038/nature12373)" value={fields.doi} onChange={(e) => updateField("doi", e.target.value)} />
                <input className="buddy-input text-xs py-2" placeholder="URL (if applicable)" value={fields.url} onChange={(e) => updateField("url", e.target.value)} />
              </div>
            )}

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: "var(--text-muted)" }}>
                <input type="checkbox" checked={showInText} onChange={() => setShowInText(!showInText)} className="accent-[var(--info)]" data-testid="cite-show-intext" />
                Show in-text citations
              </label>
            </div>

            <motion.button onClick={handleGenerate} disabled={loading || (mode === "manual" && !fields.title.trim()) || (mode === "paste" && !text.trim()) || (mode === "url" && !fields.title.trim() && !urlInput.trim())} className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-1.5" style={{ background: "var(--info)", opacity: loading ? 0.5 : 1 }} data-testid="citation-generate-btn">
              {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Wand2 size={14} /> Generate Citations</>}
            </motion.button>
          </div>

          {/* RESULT */}
          <div className="rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)", minHeight: "320px" }}>
            {loading ? (
              <div className="space-y-3"><div className="h-4 rounded animate-shimmer" style={{ width: "80%" }} /><div className="h-4 rounded animate-shimmer" style={{ width: "60%" }} /></div>
            ) : result && !result.error ? (
              <div className="space-y-3" data-testid="citation-results">
                {result.source_type && <span className="text-[10px] font-semibold px-2.5 py-0.5 rounded-full inline-block" style={{ background: "var(--info-bg)", color: "var(--info)" }}>Detected: {result.source_type}</span>}
                {STYLE_KEYS.map((s, i) => result[s.key] ? (
                  <div key={i} className="p-3 rounded-xl" style={{ background: "var(--surface-warm)" }} data-testid={`citation-${s.key}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="label-caps">{s.label}</span>
                      <button onClick={() => handleCopy(result[s.key], `c-${i}`)} className="text-[10px]" style={{ color: copied === `c-${i}` ? "var(--success)" : "var(--info)" }}>
                        {copied === `c-${i}` ? "Copied!" : "Copy"}
                      </button>
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>{result[s.key]}</p>
                    {showInText && s.intext && result[s.intext] && (
                      <p className="text-[10px] mt-1.5 italic" style={{ color: "var(--text-muted)" }}>In-text: {result[s.intext]}</p>
                    )}
                  </div>
                ) : null)}
                <button onClick={handleSaveToBibliography} className="w-full text-xs flex items-center justify-center gap-1.5 py-2 rounded-lg" style={{ background: "var(--rolex-green-light)", color: "var(--rolex-green)" }} data-testid="cite-save-btn">
                  <Plus size={12} /> Save to bibliography
                </button>
              </div>
            ) : result?.error ? (
              <p className="text-sm p-4 rounded-xl" style={{ background: "var(--error-bg)", color: "var(--error)" }} data-testid="cite-error">{result.message}</p>
            ) : (
              <div className="flex flex-col items-center justify-center py-16">
                <Quote size={36} style={{ color: "var(--surface-border-mid)" }} />
                <p className="text-sm mt-3" style={{ color: "var(--text-light)" }}>Citations will appear here</p>
                <p className="text-[11px] mt-1 text-center max-w-xs" style={{ color: "var(--text-light)" }}>Tip: paste a URL or DOI in the URL/DOI tab to auto-fill the fields.</p>
              </div>
            )}
          </div>
        </div>

        {/* BIBLIOGRAPHY */}
        {bibliography.length > 0 && (
          <div className="rounded-2xl p-6 mt-6" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }} data-testid="bib-section">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h2 className="font-display text-lg font-semibold" style={{ color: "var(--text-primary)" }}>My Bibliography ({bibliography.length})</h2>
              <div className="flex items-center gap-2">
                <select className="buddy-input text-xs py-1.5" style={{ width: 130 }} value={activeStyle} onChange={(e) => setActiveStyle(e.target.value)} data-testid="bib-style-select">
                  {STYLE_KEYS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <button onClick={exportBibliography} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: "var(--info-bg)", color: "var(--info)" }} data-testid="bib-export">
                  <Download size={12} /> Export
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {bibliography.map((b) => (
                <div key={b.id} className="p-3 rounded-lg flex items-start justify-between gap-3" style={{ background: "var(--surface-warm)" }}>
                  <p className="text-xs leading-relaxed flex-1" style={{ color: "var(--text-secondary)" }}>{b[activeStyle] || b.apa}</p>
                  <button onClick={() => removeFromBib(b.id)} className="text-[var(--text-light)] hover:text-[var(--error)] shrink-0"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
